import { Alert, Flex, Typography } from 'antd';
import type { AssetDetailIncident } from '~/app/services/asset/asset.service';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import { Loader } from '~/app/components/common/Loader/Loader';
import { formatDateTime } from './assetDetail.utils';
import { useAssetDetailIncidentColumns } from './useAssetDetailIncidentColumns';
import { useAssetDetailIncidents } from './hooks/useAssetDetailIncidents';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

type Props = {
  organizationId: string;
  assetId: string;
};

export function AssetDetailIncidentsTab({ organizationId, assetId }: Props) {
  const { t } = useTranslation('assets');
  const columns = useAssetDetailIncidentColumns();
  const {
    incidents,
    meta,
    isLoading,
    isFetching,
    error,
    refetch,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = useAssetDetailIncidents(organizationId, assetId);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={t(
          'detail.incidents.loadError',
          'Failed to load incidents for this asset.',
        )}
        action={
          <Button size="small" onClick={() => void refetch()}>
            {t('detail.incidents.retry', 'Retry')}
          </Button>
        }
      />
    );
  }

  return (
    <Flex vertical gap={16}>
      {meta.total > 0 ? (
        <Flex gap={16} wrap="wrap">
          <Typography.Text>
            {t('detail.incidents.summary.open', 'Open: {{count}}', {
              count: meta.openCount,
            })}
          </Typography.Text>
          <Typography.Text>
            {t(
              'detail.incidents.summary.highPriority',
              'Critical/high: {{count}}',
              {
                count: meta.highPriorityCount,
              },
            )}
          </Typography.Text>
          <Typography.Text>
            {t('detail.incidents.summary.latest', 'Latest: {{date}}', {
              date: meta.latestCreatedAt
                ? formatDateTime(meta.latestCreatedAt)
                : '—',
            })}
          </Typography.Text>
        </Flex>
      ) : null}
      <EvimoTable<AssetDetailIncident>
        className="ds-table"
        columns={columns}
        loading={isFetching}
        dataSource={incidents}
        rowKey="id"
        scroll={{ x: 1080 }}
        pagination={{
          current: page,
          pageSize,
          total: meta.total,
          onChange: (nextPage, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              return;
            }
            setPage(nextPage);
          },
        }}
        locale={{
          emptyText: t(
            'detail.incidents.empty',
            'No incidents are linked to this asset.',
          ),
        }}
      />
    </Flex>
  );
}
